import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ShieldAlert, CheckCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/Toast';
import { supabaseClient } from '../lib/supabase';
export function ForcePasswordChangeModal() {
  const {
    user,
    mustChangePassword,
    clearTempPassword
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!mustChangePassword || !user) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showToast('warning', 'Contraseña muy corta', 'Debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', 'No coinciden', 'Las contraseñas no son iguales');
      return;
    }
    setSaving(true);
    try {
      // Update password and remove tempPassword flag
      await supabaseClient.updateEmployee(user.employeeId || user.id, {
        password: newPassword,
        permissions: {
          ...user.permissions,
          tempPassword: false
        }
      });
      clearTempPassword();
      showToast('success', '¡Contraseña actualizada!', 'Ya puedes usar el sistema normalmente');
    } catch (error) {
      console.error('Error updating password:', error);
      showToast('error', 'Error', 'No se pudo actualizar la contraseña. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };
  const isValid = newPassword.length >= 6 && newPassword === confirmPassword;
  return <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4" data-id="element-547">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-548">
        {/* Header accent */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-500" data-id="element-549" />

        <div className="p-8" data-id="element-550">
          {/* Icon */}
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-551">
            <ShieldAlert size={32} className="text-amber-600 dark:text-amber-400" data-id="element-552" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2" data-id="element-553">
            Cambiar Contraseña
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-8 max-w-xs mx-auto" data-id="element-554">
            Tu cuenta tiene una contraseña temporal. Por seguridad, debes crear
            una nueva contraseña para continuar.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-555">
            {/* New Password */}
            <div data-id="element-556">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-557">
                Nueva Contraseña
              </label>
              <div className="relative" data-id="element-558">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-559" />
                <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Mínimo 6 caracteres" autoFocus required minLength={6} data-id="element-560" />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-561">
                  {showNew ? <EyeOff size={18} data-id="element-562" /> : <Eye size={18} data-id="element-563" />}
                </button>
              </div>
              {newPassword && newPassword.length < 6 && <p className="text-xs text-red-500 mt-1" data-id="element-564">
                  Debe tener al menos 6 caracteres
                </p>}
              {newPassword.length >= 6 && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1" data-id="element-565">
                  <CheckCircle size={12} data-id="element-566" /> Longitud válida
                </p>}
            </div>

            {/* Confirm Password */}
            <div data-id="element-567">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-568">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-569">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-570" />
                <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Repite la contraseña" required data-id="element-571" />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-572">
                  {showConfirm ? <EyeOff size={18} data-id="element-573" /> : <Eye size={18} data-id="element-574" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== newPassword && <p className="text-xs text-red-500 mt-1" data-id="element-575">
                  Las contraseñas no coinciden
                </p>}
              {confirmPassword && confirmPassword === newPassword && newPassword.length >= 6 && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1" data-id="element-576">
                    <CheckCircle size={12} data-id="element-577" /> Las contraseñas coinciden
                  </p>}
            </div>

            <Button type="submit" fullWidth disabled={!isValid || saving} className="h-12 text-base font-bold mt-2" data-id="element-578">
              {saving ? 'Guardando...' : 'Establecer Nueva Contraseña'}
            </Button>
          </form>
        </div>
      </div>
    </div>;
}